/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { updateCustomerPassword } from '@/account/api';
import { validationChangePassword } from '@/account/lib/validationChangePassword';
import { validationUniqueSymbolsPassword } from '@/account/lib/validationUniqueSymbolsPassword';
import { UseChangePasswordProps } from '@/account/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import { useCallback, useState } from 'preact/hooks';

export const DEFAULT_ERRORS_STATE = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export const useChangePassword = ({
  passwordConfigs,
  handleSetInLineAlert,
  handleHideChangePassword,
}: UseChangePasswordProps) => {
  const translations = useText({
    requiredFieldError: 'Account.FormText.requiredFieldError',
    passwordMismatch:
      'Account.minifiedView.CustomerInformation.changePassword.passwordValidationMessage.passwordMismatch',
    incorrectCurrentPassword:
      'Account.minifiedView.CustomerInformation.changePassword.passwordValidationMessage.incorrectCurrentPassword',
    passwordUpdateMessage:
      'Account.minifiedView.CustomerInformation.changePassword.passwordValidationMessage.passwordUpdateMessage',
  });
  const [isClickSubmit, setIsClickSubmit] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const hideChangePassword = useCallback(() => {
    handleHideChangePassword(() => {
      handleSetInLineAlert({});
      setPasswordErrors(DEFAULT_ERRORS_STATE);
    });
  }, [handleHideChangePassword, handleSetInLineAlert]);

  const handleCurrentPasswordChange = useCallback(
    (value: string) => {
      setCurrentPassword(value);
      setPasswordErrors((prev) => ({
        ...prev,
        currentPassword: value ? '' : translations.requiredFieldError,
      }));
    },
    [translations]
  );

  const handleNewPasswordChange = useCallback(
    (value: string) => {
      setNewPassword(value);
      setPasswordErrors((prev) => ({
        ...prev,
        newPassword: value ? '' : translations.requiredFieldError,
      }));
    },
    [translations]
  );

  const handleConfirmPasswordChange = useCallback(
    (value: string) => {
      setConfirmPassword(value);
      setPasswordErrors((prev) => ({
        ...prev,
        confirmPassword: value ? '' : translations.requiredFieldError,
      }));
    },
    [translations]
  );

  const handleOnBlurPassword = useCallback(
    (event: Event) => {
      const { name, value } = event?.target as HTMLInputElement;

      setPasswordErrors((prev) => ({
        ...prev,
        [name]: value ? '' : translations.requiredFieldError,
      }));
    },
    [translations]
  );

  const validatePasswords = useCallback(() => {
    const { isValid, errors } = validationChangePassword({
      currentPassword,
      newPassword,
      confirmPassword,
      translations,
    });

    setPasswordErrors(errors);
    return isValid;
  }, [currentPassword, newPassword, confirmPassword, translations]);

  const mutationChangePassword = useCallback(
    (event: Event) => {
      event.preventDefault();

      setSubmitLoading(true);

      const requiredCharacterClasses =
        passwordConfigs?.requiredCharacterClasses ?? 0;
      const requiredPasswordLength = passwordConfigs?.minLength ?? 1;

      if (!validatePasswords()) {
        setIsClickSubmit(true);
        setSubmitLoading(false);
        return;
      }

      if (
        !validationUniqueSymbolsPassword(
          newPassword,
          requiredCharacterClasses
        ) ||
        requiredPasswordLength > newPassword?.length
      ) {
        setIsClickSubmit(true);
        setSubmitLoading(false);

        return;
      }

      updateCustomerPassword({ currentPassword, newPassword })
        .then((result) => {
          if (!result?.length) {
            setSubmitLoading(false);
            return;
          }

          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordErrors(DEFAULT_ERRORS_STATE);
          setIsClickSubmit(false);
          handleSetInLineAlert({
            type: 'success',
            text: translations.passwordUpdateMessage,
          });
        })
        .catch((error) => {
          if (error.message === 'Invalid login or password.') {
            handleSetInLineAlert({
              type: 'error',
              text: translations.incorrectCurrentPassword,
            });
          }

          if (error.message === 'The account is locked.') {
            handleSetInLineAlert({
              type: 'error',
              text: error.message,
            });
          }
        });

      setSubmitLoading(false);
    },
    [
      passwordConfigs,
      validatePasswords,
      newPassword,
      currentPassword,
      handleSetInLineAlert,
      translations,
    ]
  );

  return {
    hideChangePassword,
    handleOnBlurPassword,
    handleConfirmPasswordChange,
    handleNewPasswordChange,
    handleCurrentPasswordChange,
    mutationChangePassword,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordErrors,
    submitLoading,
    isClickSubmit,
  };
};
